import React, { useContext, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { WatchlistProvider } from './context/WatchlistContext';
import { NotificationProvider } from './context/NotificationContext';
import Layout from './components/Layout/Layout';
import HomePage from './components/Home/HomePage';
import MoviesPage from './components/Movies/MoviesPage';
import ShowsPage from './components/Shows/ShowsPage';
import MovieDetails from './components/Movies/MovieDetails';
import ShowDetails from './components/Shows/ShowDetails';
import PlayerPage from './components/Player/PlayerPage';
import SearchPage from './components/Search/SearchPage';
import ProfilePage from './components/Profile/ProfilePage';
import UserProfilePage from './components/Profile/UserProfilePage';
import NotificationsPage from './components/Notifications/NotificationsPage';
import ErrorPage from './components/Error/ErrorPage';
import ErrorBoundary from './components/Error/ErrorBoundary';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Privacy from './pages/Privacy';
import DMCA from './pages/DMCA';
import LandingPage from './pages/LandingPage';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { saveRedirectPath, getAndClearRedirectPath } from './utils/authUtils';

// Loading spinner component
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-top: 15px;
    color: white;
    font-size: 16px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Root component to handle redirect to home
function Root() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirectedRef = useRef(false);
  
  // Use useEffect to handle navigation to avoid render cycles
  useEffect(() => {
    if (!hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      
      if (currentUser) {
        // Check if there's a saved redirect path
        const redirectPath = getAndClearRedirectPath();
        if (redirectPath) {
          // Navigate to the saved path
          navigate(redirectPath, { replace: true });
        } else {
          // Directly navigate to home with replace to avoid adding to history
          navigate('/home', { replace: true });
        }
      } else {
        // Not logged in, save the path for later redirect if it's not root
        if (location.pathname !== '/' && location.pathname !== '/home') {
          saveRedirectPath(location.pathname);
        }
        // Go to welcome page
        navigate('/welcome', { replace: true });
      }
    }
  }, [currentUser, navigate, location.pathname]);
  
  // Return null while redirecting to avoid flash of content
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppProvider>
          <WatchlistProvider>
            <NotificationProvider>
              <AppContent />
            </NotificationProvider>
          </WatchlistProvider>
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// AppContent component to handle authentication loading
const AppContent = () => {
  const { loading } = useContext(AuthContext);
  
  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <FontAwesomeIcon icon={faSpinner} />
          <p>Loading...</p>
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/dmca" element={<DMCA />} />
      
      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Layout />}>
          <Route index element={<Root />} />
          <Route path="home" element={
            <ErrorBoundary>
              <HomePage />
            </ErrorBoundary>
          } />
          <Route path="movies" element={
            <ErrorBoundary>
              <MoviesPage />
            </ErrorBoundary>
          } />
          <Route path="shows" element={
            <ErrorBoundary>
              <ShowsPage />
            </ErrorBoundary>
          } />
          <Route path="movie/:id" element={
            <ErrorBoundary>
              <MovieDetails />
            </ErrorBoundary>
          } />
          <Route path="show/:id" element={
            <ErrorBoundary>
              <ShowDetails />
            </ErrorBoundary>
          } />
          <Route path="player" element={
            <ErrorBoundary>
              <PlayerPage />
            </ErrorBoundary>
          } />
          <Route path="search" element={
            <ErrorBoundary>
              <SearchPage />
            </ErrorBoundary>
          } />
          <Route path="profile" element={
            <ErrorBoundary>
              <ProfilePage />
            </ErrorBoundary>
          } />
          <Route path="profile/:username" element={
            <ErrorBoundary>
              <UserProfilePage />
            </ErrorBoundary>
          } />
          <Route path="notifications" element={
            <ErrorBoundary>
              <NotificationsPage />
            </ErrorBoundary>
          } />
          <Route path="*" element={<ErrorPage />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default App;